import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { Table, type ColumnDef } from "@/components/table";
import { useCompanyGetEventParticipantsQuery } from "@/services/(company)/api.events";
import { EVENT_INVITATION_STATUSES } from "@/types/(company)/events.interface";
import { useLocale } from "@/hooks/useLocale";
import { startCase } from "lodash";
import { useSearchPageParams } from "@/hooks/useTableParams";

const buildColumns = (t: TFunction): ColumnDef<Record<string, unknown>>[] => [
  { key: "name", label: t("common:labels.name"), width: "50%" },
  {
    key: "invitationStatus",
    label: t("events:shared.invitation_status"),
    width: "25%",
  },
  { key: "respondedAt", label: t("events:shared.responded_at"), width: "25%" },
];

export const EventParticipants = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation(["common", "events"]);
  const { formatDate } = useLocale();
  const columns = buildColumns(t);
  // Search and page live in the URL — see useTableParams.
  const tp = useSearchPageParams();

  const { data, isFetching } = useCompanyGetEventParticipantsQuery(
    // Spread, not an inline key: this endpoint takes `pageSize`, which the
    // shared `IApiGeneralParams` arg type doesn't declare.
    {
      eventId,
      page: tp.page,
      search: tp.committedSearch,
      ...({ pageSize: 10 } as Record<string, number>),
    },
    { skip: !eventId },
  );
  const participants = data?.data.participants;

  const rows =
    participants?.map((participant) => ({
      name: (
        <div className="flex items-center gap-2">
          <Avatar>
            <AvatarImage src={participant.profileImage ?? undefined} />
            <AvatarFallback>{participant.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <p className="font-medium capitalize">{participant.name}</p>
          {participant.isExternal && (
            <Badge variant="info" className="ms-1">
              {t("events:shared.external_badge")}
            </Badge>
          )}
        </div>
      ),
      invitationStatus: <StatusPill status={participant.status} />,
      respondedAt: participant.respondedAt
        ? formatDate(new Date(participant.respondedAt), "dd MMM yyyy, hh:mm a")
        : t("events:shared.not_responded_yet"),
    })) ?? [];

  return (
    <div className="flex flex-col gap-3">
      <SectionTitle
        title={t("common:a11y.event_participants_and_reponses")}
        size="sm"
      />
      <div className="flex flex-col h-[480px] rounded-md border border-border overflow-hidden">
        <Table
          data={rows}
          loading={isFetching}
          searchText={tp.search}
          columns={columns}
          pagination={{
            totalPages: data?.meta?.totalPages,
            currentPage: tp.page,
            onPageChange: tp.setPage,
          }}
          onSearchChange={tp.setSearch}
          searchPlaceholder={t("events:shared.search_by_invitee_hint")}
        />
      </div>
    </div>
  );
};

const StatusPill = ({ status }: { status: EVENT_INVITATION_STATUSES }) => (
  <Badge variant={statusToVariant(status)}>{startCase(status)}</Badge>
);
